var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { ElementRef } from '@angular/core';
import { LabelModel } from './models/labels/label.model';
import { uuid } from '../shared/uuid';
import { LabelPinType } from './models/labels/label-pin-type.enum';
import { LabelThemeEnum } from './models/labels/label-theme.enum';
import { HotspotModel } from './models/labels/hotspot.model';
import { HotspotThemesEnum } from './models/labels/hotspot.themes.enum';
import { LabelStateModel } from './models/labels/label-state.model';
import { FlyToObjectErrandModel } from './models/errands/fly-to-object-errand.model';
import { FlyToCameraErrandModel } from './models/errands/fly-to-camera-errand.model';
import { classToClass } from 'class-transformer';
import { HotspotTypeEnum } from './models/labels/hotspot.types.enum';
import { EngineService } from '../engine/engine.service';
import { ErrandType } from './models/errands/errand-type.enum';
import { FlyToChapterErrandModel } from './models/errands/fly-to-chapter-errand.model';
import * as i0 from "@angular/core";
import * as i1 from "../engine/engine.service";
var errandTypeToHotspotAction = {
    'flyToCamera': 'flyTo',
    'flyToChapter': 'flyTo'
};
function translateLabelActionToHotspot(type) {
    return errandTypeToHotspotAction[type] || type;
}
/*
Handles some ugliness in the two-way conversion of legacy annotations and
hotspots into newer labels.
*/
var LabelsService = /** @class */ (function () {
    function LabelsService(engineService) {
        var _this = this;
        this.engineService = engineService;
        // convert legacy action into equivalent errand
        this.convertActionToErrand = function (moduleData, chapterIndex, action) {
            var flyToAction = new RegExp('^flyTo(Async)?');
            var flyToObjectAction = new RegExp('^flyToObject(Async)?');
            var flyToChapterAction = new RegExp('^flyTo[\w\.\:]+currentChapter\.[flyTo|jumpTo]');
            // currently we only support flyTo
            var tokens = action.split(':').slice(0, 2); // we only take action and one parameter
            // conditionally parse where in the data the action target value is located
            var actionScope = moduleData;
            var actionTarget = tokens[1]; // address is scoped to module data
            if (/currentChapter/.test(actionTarget)) {
                actionTarget = actionTarget.replace('currentChapter', "chapters[" + chapterIndex + "]");
            }
            // build errand based on action
            var errand = null;
            if (flyToObjectAction.test(tokens[0])) {
                errand = new FlyToObjectErrandModel(_this.objectAddress(actionScope, actionTarget));
            }
            else if (flyToAction.test(tokens[0])) {
                errand = new FlyToCameraErrandModel(_this.objectAddress(actionScope, actionTarget));
            }
            else if (flyToChapterAction.test(tokens[0])) {
                errand = new FlyToChapterErrandModel();
            }
            return errand;
        };
        // convert errand into equivalent legacy action
        this.convertErrandToAction = function (label, errand) {
            var id = uuid.idWithPrefix('errand').replace(/\-/g, '_');
            var data = {};
            var action = '';
            var type = translateLabelActionToHotspot(errand.type);
            if (errand.type === ErrandType.flyToChapter) {
                // there is no flyToChapter action. We have to pass in the address to the chapter's camera (jupTy or flyTo) property
                action = type + "Async:currentChapter." + _this.engineService.getChapter(label.chapterId).camera.action;
            }
            else {
                action = type + "Async:currentChapter.customData." + id,
                    data[id] = errand.value;
            }
            return {
                action: action,
                data: data
            };
        };
        // extract sub-object from object given dot-notation address
        this.objectAddress = function (srcObject, address) {
            return address.split('.').reduce(function (o, i) {
                if (o === null) {
                    return null;
                }
                else if (/\[+[0-9+]\]+/.test(i)) {
                    // access array element
                    var parts = i.split('[');
                    var idx = Number(parts[1].match(/[0-9]+/));
                    if (parts[0] in o && idx in o[parts[0]]) {
                        return o[parts[0]][idx];
                    }
                    else {
                        return null;
                    }
                }
                else if (i in o) {
                    return o[i];
                }
                else {
                    return null;
                }
            }, srcObject);
        };
    }
    // DESERIALIZATION =====================================================================
    // returns true if hotspot can be interpreted as a label (ignore show/hide hotspot)
    LabelsService.prototype.filterHotspot = function (moduleData, chapter, hotspot) {
        var supportedActions = new RegExp('^(flyTo|flyToObject|hideHotspot|showHotspot|resetHotspots)');
        // get filtered actions
        var actions = this.getHotspotAction(moduleData, hotspot);
        var filtered = actions.filter(function (_action) { return supportedActions.test(_action); });
        if (actions.length === filtered.length) {
            return true;
        }
        else {
            return false;
        }
    };
    // deserialize legacy annotations into labels
    LabelsService.prototype.deserializeAnnotation = function (annotation, chapter, theme) {
        var label = new LabelModel(annotation.title || '');
        // identifyin information
        label.id = annotation.annotationId || uuid.idWithPrefix('label');
        label.chapterId = chapter.id;
        // style info
        label.expandTag = true;
        label.showDescription = true;
        label.collapseDescription = false;
        label.pinGlow = false;
        label.hideWire = false;
        label.hideTag = false;
        if (theme === LabelThemeEnum.Annotation) {
            label.theme = LabelThemeEnum.Annotation;
            label.pinType = LabelPinType.Default;
            label.flyTo = false;
            label.states = [];
        }
        else {
            // annotation -> studio label
            label.theme = LabelThemeEnum.StudioNext;
            label.pinType = LabelPinType.PlusMinus;
            label.flyTo = true;
            label.states = [
                new LabelStateModel(),
                new LabelStateModel()
            ];
            label.states[0].errands = [new FlyToCameraErrandModel(chapter.camera.location)];
            label.states[1].errands = [new FlyToObjectErrandModel(annotation.objectId)];
        }
        label.occludable = annotation.occludable !== false; // default to true
        label.renderableIndex = annotation.renderableIndex || 0;
        if (annotation.objectId && annotation.hasOwnProperty('barycentric')) {
            // use barycentric coordinate
            label.objectId = annotation.objectId;
            label.primitiveIndex = annotation.primitiveIndex;
            label.barycentric = annotation.barycentric;
        }
        else if (annotation.hasOwnProperty('pos')) {
            // world position (let the marker deal with it)
            if (annotation.hasOwnProperty('objectId')) {
                label.objectId = annotation.objectId;
            }
            label.pos = annotation.pos;
        }
        // direction
        if (annotation.dir && Array.isArray(annotation.dir)) {
            label.dir = annotation.dir;
        }
        else if (annotation.dir && annotation.dir instanceof Object) {
            label.dir = Object.values(annotation.dir);
        }
        // text label information
        label.description = annotation.description || '';
        label.offset = annotation.labelOffset || [40, 40];
        // set shown
        label.shown = annotation.shown !== false;
        label.labelShown = annotation.labelShown !== false;
        return label;
    };
    // converts one (or two, if paired)  hotspots into a label
    LabelsService.prototype.deserializeHotspots = function (moduleData, chapter, chapterIndex) {
        var _this = this;
        var hotspots = [];
        for (var _i = 3; _i < arguments.length; _i++) {
            hotspots[_i - 3] = arguments[_i];
        }
        var _a = __read(hotspots, 2), primaryHotspot = _a[0], secondaryHotspot = _a[1];
        // label identification
        var label = new LabelModel(primaryHotspot.title || '');
        label.id = uuid.idWithPrefix('hotspot');
        label.objectId = primaryHotspot.objectId;
        label.chapterId = chapter.id;
        // display information
        label.description = primaryHotspot.description || '';
        label.pinType = /plus|minus/.test(primaryHotspot.className) ? LabelPinType.PlusMinus : LabelPinType.Default;
        label.pinGlow = /pulse/.test(primaryHotspot.className);
        label.occludable = true;
        label.shown = primaryHotspot.shown;
        label.offset = primaryHotspot.labelOffset ? primaryHotspot.labelOffset : [22, -17];
        if (/left/.test(primaryHotspot.className) && label.offset[0] > 0) {
            label.offset[0] *= -1;
        }
        else if (/right/.test(primaryHotspot.className) && label.offset[0] < 0) {
            label.offset[0] *= -1;
        }
        // positioning
        if (primaryHotspot.hasOwnProperty('barycentric')) {
            // use shape centric coordinate
            label.renderableIndex = primaryHotspot.renderableIndex || 0;
            label.barycentric = primaryHotspot.barycentric;
            label.primitiveIndex = primaryHotspot.primitiveIndex;
        }
        else {
            // use world pstiion (let marker handle positioning)
            label.pos = primaryHotspot.pos;
        }
        label.dir = primaryHotspot.dir;
        // diplay theme:
        primaryHotspot.theme = !!primaryHotspot.theme ? primaryHotspot.theme : HotspotThemesEnum.classicHotspot;
        switch (primaryHotspot.theme) {
            case HotspotThemesEnum.classicAnnotation:
                label.theme = LabelThemeEnum.Annotation;
                label.pinType = LabelPinType.Default; // force set to circle if annotation
                label.showDescription = true;
                label.collapseDescription = false;
                label.expandTag = true;
                label.hideTag = false;
                label.hideWire = false;
                break;
            case HotspotThemesEnum.classicHotspot:
                label.theme = LabelThemeEnum.Classic;
                label.showDescription = /label/.test(primaryHotspot.className);
                label.expandTag = /label/.test(primaryHotspot.className);
                label.hideTag = !label.expandTag;
                label.collapseDescription = false; // default to show description
                label.hideWire = !/leader-line/.test(primaryHotspot.className);
                break;
            case HotspotThemesEnum.studioNext:
                label.theme = LabelThemeEnum.StudioNext;
                label.showDescription = !!primaryHotspot.description;
                label.collapseDescription = /collapsible/g.test(primaryHotspot.className);
                label.hideTag = !primaryHotspot.title;
                label.hideWire = !/leader-line/.test(primaryHotspot.className);
                break;
            default:
                break;
        }
        // actions (first action)
        label.flyTo = false;
        label.flyToTarget = null;
        label.states = [];
        var actions1 = this.filterHotspotActions(this.getHotspotAction(moduleData, primaryHotspot));
        var actions2 = !!secondaryHotspot ? this.filterHotspotActions(this.getHotspotAction(moduleData, secondaryHotspot)) : [];
        var state1 = new LabelStateModel();
        var state2 = new LabelStateModel();
        if (actions1.length) {
            state1.errands = actions1.map(function (action) { return _this.convertActionToErrand(moduleData, chapterIndex, action); });
        }
        if (actions2.length) {
            state2.errands = actions2.map(function (action) { return _this.convertActionToErrand(moduleData, chapterIndex, action); });
        }
        label.states = [state2, state1];
        return label;
    };
    // CONVERSION ===========================================================================
    LabelsService.prototype.makeDefaultStudioLabel = function (sourceLabel) {
        var label = sourceLabel ? __assign({}, sourceLabel) : new LabelModel('');
        label.pinType = LabelPinType.PlusMinus;
        label.theme = LabelThemeEnum.StudioNext;
        label.expandTag = true;
        label.showDescription = true;
        label.collapseDescription = false;
        label.pinGlow = false;
        label.hideWire = false;
        label.hideTag = false;
        label.states = [
            new LabelStateModel(),
            new LabelStateModel()
        ];
        return label;
    };
    LabelsService.prototype.makeAnnotationLabel = function (sourceLabel) {
        var label = sourceLabel ? __assign({}, sourceLabel) : new LabelModel('');
        label.pinType = LabelPinType.Default;
        label.theme = LabelThemeEnum.Annotation;
        label.expandTag = true;
        label.showDescription = true;
        label.collapseDescription = false;
        label.pinGlow = false;
        label.hideWire = false;
        label.hideTag = false;
        label.states = [];
        return label;
    };
    // SERIALIZATION ========================================================================
    // converts one label into one (or two, if paired) hotspots
    LabelsService.prototype.serializeLabel = function (label, idx, reverseNamespaces) {
        var _this = this;
        var hotspot = new HotspotModel();
        var labelCopy = classToClass(label);
        // basic
        var className = ['circle', 'draggable'];
        hotspot.shown = true;
        // identification
        if (label.objectId) {
            hotspot.objectId = labelCopy.objectId;
        }
        // position and direction. #TODO: [EM] support hotspot barycentric positioning?
        if (label.primitiveIndex && label.barycentric && label.objectId) {
            hotspot.renderableIndex = label.renderableIndex || 0;
            hotspot.primitiveIndex = label.primitiveIndex;
            hotspot.barycentric = label.barycentric;
            hotspot.pos = this.engineService.barycentricToWorld(reverseNamespaces ? reverseNamespaces[label.objectId] : label.objectId, label.primitiveIndex, label.barycentric);
        }
        else {
            hotspot.pos = labelCopy.pos;
        }
        if (!!label.dir) {
            // NOTE: The direction vector is a float-32-array
            hotspot.dir = Array.prototype.slice.call(label.dir);
        }
        hotspot.labelOffset = label.offset;
        // display information
        switch (label.theme) {
            case LabelThemeEnum.Annotation:
                hotspot.theme = HotspotThemesEnum.classicAnnotation;
                break;
            case LabelThemeEnum.Classic:
                hotspot.theme = HotspotThemesEnum.classicHotspot;
                break;
            default:
                hotspot.theme = HotspotThemesEnum.studioNext;
        }
        if (labelCopy.title !== '') {
            hotspot.title = labelCopy.title;
            hotspot.description = labelCopy.description;
            hotspot.type = HotspotTypeEnum.CircleLabel;
        }
        else {
            hotspot.type = HotspotTypeEnum.Circle;
        }
        // hotspot classes
        if (label.pinGlow) {
            className.push('pulse');
        }
        if (label.pinType === LabelPinType.PlusMinus) {
            className.push('plus');
        }
        if (!label.hideWire) {
            className.push('leader-line');
        }
        if (!label.hideTag) {
            className.push('label');
        }
        if (label.collapseDescription) {
            className.push('collapsible collapsed');
        }
        hotspot.className = className.join(' ');
        // hotspot actions & data
        var data = {}; // customData blocked to current chapter, used by hotspots for action targets
        hotspot.actions = [];
        var hotspots = [hotspot];
        var stateLength = label.states.length;
        label.states.forEach(function (state, stateIndex) {
            // NOTE: [EM] a little extra complex here b/c states and hotspot actions are kind of 'inverses' of eachother.
            if (stateIndex === stateLength - 1) {
                // add actions to this hotspot
                hotspots[0].actions.push('resetHotspots');
                state.errands.forEach(function (errand) {
                    var action = _this.convertErrandToAction(label, errand);
                    hotspots[0].actions.push(action.action);
                    data = __assign({}, data, action.data);
                });
            }
            else {
                // we need new hotspots
                var secondaryHotspot_1 = classToClass(hotspot);
                secondaryHotspot_1.actions = [];
                secondaryHotspot_1.className = secondaryHotspot_1.className.replace('plus', 'minus');
                state.errands.forEach(function (errand) {
                    var action = _this.convertErrandToAction(label, errand);
                    secondaryHotspot_1.actions.push(action.action);
                    data = __assign({}, data, action.data);
                });
                hotspots.push(secondaryHotspot_1);
            }
        });
        // teach plus two hotspots to toggle
        if (hotspots.length === 2) {
            // hotspot 1 : hide 1, show 2
            hotspots[0].actions.push("hideHotspots:currentChapter.hotspots[" + idx + "]");
            hotspots[0].actions.push("showHotspots:currentChapter.hotspots[" + (idx + 1) + "]");
            // hotspot 2: hide 2, show 1
            hotspots[1].actions.push("hideHotspots:currentChapter.hotspots[" + (idx + 1) + "]");
            hotspots[1].actions.push("showHotspots:currentChapter.hotspots[" + idx + "]");
            hotspots[0].shown = true;
            hotspots[1].shown = false;
        }
        if (hotspots.length === 2 && label.collapseDescription) {
            hotspots[1].className += hotspots[1].className.replace('collapsed', '');
        }
        // ensure left/right class
        hotspots.forEach(function (_hotspot) {
            if (hotspot.labelOffset[0] < 0) {
                _hotspot.className = _hotspot.className.replace('right', '') + ' left';
            }
            else if (hotspot.labelOffset[0] > 0) {
                _hotspot.className = _hotspot.className.replace('left', '') + ' right';
            }
        });
        return {
            data: data,
            hotspots: hotspots
        };
    };
    LabelsService.prototype.setLabelContainer = function (container) {
        this._labelContainer = container;
    };
    LabelsService.prototype.getLabelContainer = function () {
        return this._labelContainer;
    };
    // HELPER FUNCTIONS ======================================================================
    // parse out actions array from hotspot, given module and chapter scopes
    LabelsService.prototype.getHotspotAction = function (moduleData, hotspot) {
        var actions = !!hotspot.actionSrc ? this.objectAddress(moduleData, hotspot.actionSrc) : (hotspot.actions ? hotspot.actions : []);
        if (actions instanceof Array) {
            return actions;
        }
        else {
            return [];
        }
    };
    // filter for hotspot actions that are supported in studio
    LabelsService.prototype.filterHotspotActions = function (actions) {
        var supportedActions = new RegExp('^(flyTo|flyToObject)');
        return actions.filter(function (_action) { return supportedActions.test(_action); });
    };
    LabelsService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function LabelsService_Factory() { return new LabelsService(i0.ɵɵinject(i1.EngineService)); }, token: LabelsService, providedIn: "root" });
    return LabelsService;
}());
export { LabelsService };
